/** Typed client for the docsdone API. */

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? ''

function apiUrl(path: string): string {
  return `${API_BASE}${path}`
}

/**
 * The API is served from a different host than the SPA in every deployed
 * environment, so the sender session cookie only travels when the request opts
 * into credentials and the server answers with Access-Control-Allow-Credentials.
 * Without both halves the authenticated surface returns 401 everywhere except
 * local development, where the Vite proxy makes everything same-origin.
 *
 * The signer's cookie needs it just as badly: the signing session lives in a
 * cookie the exchange sets, and every later signer call is refused without it.
 */
const creds: RequestInit = { credentials: 'include' }

export interface PageInfo {
  number: number
  width: number
  height: number
  rotate: number
  origin_x: number
  origin_y: number
}

export interface NormRect {
  x: number
  y: number
  w: number
  h: number
}

export interface UploadResult {
  document_key: string
  sha256: string
  bytes: number
  page_count: number
  pages: PageInfo[]
  /** False for pre-1.4 PDFs, which can only carry signature boxes. */
  supports_fields: boolean
}

/** How docsdone got a signing link to the person who has to sign. */
export type Delivery = 'returned_to_sender' | 'emailed_by_docsdone'

/** How somebody proved who they were. */
export type AuthMethod =
  | 'email_link'
  | 'email_otp'
  | 'federated_oidc'
  | 'service_credential'
  | 'sms_otp'
  | 'access_code'
  | 'kba'
  | 'government_id'
  | 'digital_identity'
  | 'none'

export type ParticipationStatus = 'pending' | 'viewed' | 'signed' | 'declined'

export interface Envelope {
  id: string
  organization_id?: string
  title: string
  status: 'draft' | 'sent' | 'completed' | 'declined' | 'voided'
  document_sha256: string
  document_bytes: number
  page_count: number
  signed_sha256?: string
  signed_bytes?: number
  certificate_sha256?: string
  certificate_bytes?: number
  retention_days: number
  expires_at?: string
  legal_hold: boolean
  /**
   * Whether docsdone chases this envelope's signers on its own, at one, three
   * and seven days after their link went out. The sender's choice at send.
   */
  reminders_enabled: boolean
  purged_at?: string
  created_at: string
  updated_at: string
  completed_at?: string
  /**
   * Who created it and how they were signed in, read from the chain. The
   * listing leaves it out rather than fold a chain per row.
   */
  sent_by?: { name: string; identifier: string; method: AuthMethod }
  /** Gates the Certificate button. The route itself is still the authority. */
  certificate_ready: boolean
  /** How this member came to see it, on the listing only. */
  via?: 'membership' | 'participation'
  /**
   * Who was asked to act, and where each of them has got to. On the listing
   * only — a single envelope carries the full participations array instead.
   * Signing groups collapse to one chip.
   */
  recipients?: ListingRecipient[]
}

/** One person on a dashboard row. Workflow status, not evidence. */
export interface ListingRecipient {
  name: string
  role: string
  status: string
}

/** Where one person on an envelope has got to. Workflow, not evidence. */
export interface ParticipationState {
  status: ParticipationStatus
  viewed_at?: string
  consented_at?: string
  signed_at?: string
  declined_at?: string
  decline_reason?: string
  /** SHA-256 of the document as this particular signer sealed it. */
  signed_document_sha256?: string
}

/**
 * One person's place on one envelope. The name and the address are what the
 * sender presented, which is why they are recorded as presented and never
 * substituted for anything docsdone believes instead.
 */
export interface Participation {
  id: string
  envelope_id: string
  role: string
  routing_order: number
  position: number
  name: string
  identifier_kind: string
  identifier: string
  identifier_origin: string
  auth_requirements?: string
  signing_group_id?: string
  signing_group_label?: string
  created_at: string
  state?: ParticipationState
  /**
   * The link as it stands: the latest one issued for this participation,
   * whether or not it has since been withdrawn. Absent for the sender, who is
   * never sent one. `revoked` is what tells a sender to reissue.
   */
  link?: { delivery: Delivery; expires_at: string; revoked: boolean }
  /**
   * Whether the envelope is waiting on this person right now: their turn is
   * open and they have not done what was asked. The server folds the chain
   * to answer it, which is the same answer the signing path runs on, so the
   * page no longer guesses from the roster order.
   */
  waiting_on: boolean
  /**
   * How the chasing stands, for the people somebody waits on. Absent for the
   * sender and for copy recipients. `why_not` is the server's reason docsdone
   * cannot remind them, when it cannot.
   */
  reminders?: ParticipationReminders
}

export interface ParticipationReminders {
  sent: number
  last_at?: string
  next_at?: string
  why_not?: 'no_emailed_link' | 'link_bounced' | 'link_expired' | string
}

export interface Field {
  id: string
  envelope_id: string
  participation_id: string
  /** The document this box is on, matching an EnvelopeDocument's id. */
  envelope_item_id: string
  kind: FieldKind
  label?: string
  options?: string[]
  page: number
  norm_x: number
  norm_y: number
  norm_w: number
  norm_h: number
  required: boolean
  value_text?: string
  filled_at?: string
  /** Hide this box until another box has a particular answer. */
  show_when?: { field_id: string; op: string; value?: string }
}

/**
 * One entry on the evidence chain. An act is something a person did, a seal is
 * something docsdone did to the document, and the hashes are what makes the
 * sequence tamper-evident.
 */
export interface AuditEvent {
  seq: number
  format_version: number
  kind: 'act' | 'service_act' | 'seal' | 'system'
  type: string
  participation_id?: string
  identifier_presented?: string
  name_presented?: string
  auth_method: AuthMethod
  verifier: string
  evidence: Record<string, unknown>
  data: Record<string, unknown>
  ip?: string
  user_agent?: string
  /** The chain's own fixed-width text, hashed as written. Not a Date. */
  occurred_at: string
  hash: string
  prev_hash: string
}

export interface ChainVerification {
  valid: boolean
  event_count: number
  head_hash?: string
  broken_at?: number
  reason?: string
  formats?: number[]
}

export interface RoutingCondition {
  field_id: string
  op: 'gt' | 'eq' | 'checked'
  value?: string
}

export interface RoutingRuleView {
  id: string
  after_participation_id: string
  condition: RoutingCondition
  if_true_skip: string[]
  if_false_skip: string[]
}

export interface RoutingDecisionView {
  seq: number
  occurred_at: string
  rule_id: string
  after_participation_id: string
  condition: RoutingCondition
  field_value: string
  matched: boolean
  skipped: string[]
}

export interface RoutingDetail {
  rules?: RoutingRuleView[]
  skipped?: string[]
  decisions?: RoutingDecisionView[]
}

export interface RoutingRuleInput {
  after_recipient_index: number
  condition: {
    recipient_index: number
    field_index: number
    op: 'gt' | 'eq' | 'checked'
    value?: string
  }
  if_true_skip: number[]
  if_false_skip: number[]
}

/** One of an envelope's documents. Every envelope has at least one. */
export interface EnvelopeDocument {
  id: string
  envelope_id: string
  position: number
  name: string
  document_sha256: string
  document_bytes: number
  page_count: number
  /** Present once somebody has sealed this document. */
  signed_sha256?: string
  signed_bytes?: number
}

export interface EnvelopeDetail {
  envelope: Envelope
  documents: EnvelopeDocument[]
  participations: Participation[]
  fields: Field[]
  audit: AuditEvent[]
  chain: ChainVerification
  routing?: RoutingDetail
}

export interface SignerReport {
  name: string
  reason: string
  location: string
  contact_info: string
  valid_signature: boolean
  trusted_issuer: boolean
  /**
   * A revocation statement inside the file names a certificate this signature
   * carries. Nobody asks the issuer, so false never means "not revoked".
   */
  revoked_certificate: boolean
  signature_time?: string
  timestamp_status?: string
  certificate_subject?: string
  certificate_issuer?: string
  /**
   * An RFC 3161 document timestamp: an authority's token showing the bytes
   * existed at a time. A signature by nobody; certificate_subject is the
   * authority.
   */
  document_timestamp?: boolean
  /**
   * Which version of the file it covers: the file as it is now, or an earlier
   * version, and then whether a later signature that verifies and reaches the
   * end covers what was added after it — for a trusted signature, a trusted
   * one, or else only untrusted ones do. Absent when its range cannot be
   * spoken for.
   */
  covers?: 'whole_file' | 'earlier_version_signed_after' | 'earlier_version_signed_after_untrusted' | 'earlier_version'
}

export interface VerificationReport {
  /** Every signature still matches the bytes it signed. */
  intact: boolean
  /**
   * A seal a reader can rely on reaches the end of the file: a trusted one
   * when any seal is trusted, otherwise any intact one, and never a document
   * timestamp. False with intact true means content was added after it.
   */
  covers_whole_file: boolean
  trusted_chain: boolean
  signers: SignerReport[] | null
  error?: string
  /** 'too_many_signatures': more than docsdone checks, so none was checked. */
  code?: string
  known_to_us?: boolean
}

export interface SignFieldView {
  id: string
  kind: FieldKind
  label?: string
  options?: string[]
  /** Which of the envelope's documents this box is on, counting from zero. */
  document_index: number
  page: number
  rect: NormRect
  required: boolean
  /** Whether this box has already been filled by a signature of this person. */
  filled: boolean
  /** What the box starts with: initials from the name, the date as it will be stamped. */
  prefill?: string
  /** False for values the server fills, such as the signing date. */
  editable: boolean
  /** Hide this box until another box has a particular answer. */
  show_when?: { field_id: string; op: string; value?: string }
}

/** One other person on the envelope, as shown to a signer. */
export interface PartyMember {
  name: string
  routing_order: number
  status: ParticipationStatus
  you: boolean
  /**
   * The sender's routing took this person off the signing path, so a list of
   * who is on the document does not call them waiting. Absent from a server
   * older than the field.
   */
  skipped?: boolean
  /**
   * What the party is on the document to do: sign (a signer, a witness or a
   * signing group), only open it, or get a copy. Absent from a server older
   * than the field, and the list then keeps its old words.
   */
  to?: 'sign' | 'view' | 'copy'
  /**
   * This person is a member of a signing group and another member signed for
   * the group, so nothing is waited for from them. Their own status stays
   * what they did (nothing, or opened). Absent from a server older than the
   * field, which reads as false (internal/api/sign.go partyView).
   */
  filled_by_group?: boolean
}

/**
 * One party a person's turn is waiting for, and what they still have to do:
 * sign, for a signer, a witness or a signing group, or only open the
 * document. A group is one entry, named as the sender named it, with the
 * members any one of whom may sign for it. No address and no participation
 * id (internal/api/sign.go waitingForView).
 */
export interface WaitingFor {
  name: string
  routing_order: number
  to: 'sign' | 'view'
  members?: string[]
}

/**
 * How a signer is also signed in to docsdone, when they are: the member
 * session that corroborated their signing session (internal/api/sign.go
 * corroborationView). method is that session's own. provider names who
 * vouched for a federated one, and only where the server's mapping knows a
 * name, the mapping the certificate's own sentence uses. brokered says the
 * organization's own identity provider asserted the address.
 */
export interface SignerCorroboration {
  method: AuthMethod
  provider?: string
  brokered?: boolean
}

/** Who a signing session belongs to, and how that person was authenticated. */
export interface SignerIdentity {
  participation_id: string
  name: string
  identifier: string
  role: string
  routing_order: number
  authentication?: {
    method: AuthMethod
    delivery: Delivery
    corroborated: boolean
    /** Present only when corroborated. A sentence about it is worded from this and nothing else. */
    corroboration?: SignerCorroboration
  }
}

/**
 * What a signing link is worth on its own: the right to see who asked for what
 * and to ask for a session. Reading it records nothing, which is why a mail
 * scanner opening the link leaves no trace.
 */
export interface Invitation {
  envelope: { id: string; title: string; status: string }
  sender: { name: string; identifier: string }
  you: SignerIdentity
  party: PartyMember[]
  your_turn: boolean
  already_signed: boolean
  /** Set when another member of the same signing group signed first. */
  group_filled_by?: string
  declined: boolean
  consented: boolean
  certificate_ready: boolean
  /**
   * Who this person's turn is waiting for, in routing order. It is the rule
   * your_turn is answered by, so it is empty exactly when your_turn is true or
   * skipped is. It describes the turn and nothing else: whether the document
   * is still open is envelope.status. Absent from a server older than the
   * field, and a page then reads the party list as it did before.
   */
  waiting_for?: WaitingFor[]
  /**
   * Every signing slot the document still needs a signature in, in routing
   * order: signers and witnesses who have not signed, a signing group nobody
   * has signed for as one entry with its members. Never a viewer, a copy
   * recipient, the sender or anybody routing skipped. It includes this
   * person until they sign. `[]` when nobody is left. Absent from a server
   * older than the field; a page then reads the party list.
   */
  still_to_sign?: WaitingFor[]
  /**
   * The sender's routing took this person off the signing path: their turn
   * never opens, and the document can finish without them. your_turn stays
   * false. Absent from a server older than the field.
   */
  skipped?: boolean
  invitation_expires_at: string
  delivery: Delivery
  step_up: { required: boolean; method: 'email_otp' | null; hint?: string }
  session: { active: boolean; expires_at?: string }
}

/**
 * The short, revocable session a link is exchanged for. It lives in a cookie
 * the server sets; every act the signer takes is recorded against it.
 *
 * pages is a plain list of page numbers. The page sizes come from the PDF
 * itself, because answering this needs no envelope row and reads no blob.
 */
/** One document of the envelope, as the signer is shown it. */
export interface SignDocumentView {
  index: number
  name: string
  page_count: number
  pages: number[]
  /** Whether this person has a box on it. Every document is listed either way. */
  yours: boolean
}

export interface SignSession {
  you: SignerIdentity
  session: { expires_at: string }
  fields: SignFieldView[]
  field: SignFieldView | null
  /** Every document this person has a box on, in the envelope's reading order. */
  documents: SignDocumentView[]
  pages: number[]
  page_count: number
  consented: boolean
}

/** The kinds of box a sender can place on a document. */
export type FieldKind = 'signature' | 'initials' | 'date_signed' | 'text' | 'checkbox' | 'radio' | 'dropdown'

export interface EnvelopeFieldInput {
  kind: FieldKind
  /** Which document this box is on. Absent means the first. */
  document_index?: number
  page: number
  rect: NormRect
  required?: boolean
  label?: string
  options?: string[]
  /**
   * Hide this box until another box has a particular answer. Named by
   * recipient and field index because ids do not exist until send.
   */
  show_when?: { recipient_index: number; field_index: number; op: string; value?: string }
}

export type RecipientRole = 'signer' | 'cc' | 'needs_to_view' | 'witness'

export interface EnvelopeGroupMemberInput {
  name: string
  email: string
}

export interface EnvelopeRecipientInput {
  name: string
  email?: string
  role?: RecipientRole
  routing_order: number
  fields: EnvelopeFieldInput[]
  /** When set, any one member may sign on behalf of the group. Name is the label. */
  members?: EnvelopeGroupMemberInput[]
  /** Asking for anything here is refused for now, not quietly dropped. */
  auth_requirements?: { methods: string[] }
}

/**
 * A recipient as created. The link is what the sender hands on: in this
 * release docsdone always gives it back to them rather than emailing it, so
 * signing_url is present and sent_to is not.
 */
export interface CreatedRecipient {
  participation_id: string
  name: string
  email: string
  role: RecipientRole
  signing_group_id?: string
  signing_group_label?: string
  link?: { delivery: Delivery; expires_at: string; revoked: false }
  signing_url?: string
  sent_to?: string
  delivered?: boolean
  fields?: { id: string; kind: FieldKind; page: number; rect: NormRect }[]
}

export interface CreateEnvelopeResult {
  envelope: Envelope
  recipients: CreatedRecipient[]
}

export interface Template {
  id: string
  organization_id: string
  created_by_party_id?: string
  name: string
  document_sha256: string
  /** The first document's size and pages; the totals below are all of them. */
  document_bytes: number
  page_count: number
  retention_days: number
  created_at: string
  updated_at: string
  /** How many documents the template holds, and their pages and bytes together. */
  document_count?: number
  total_page_count?: number
  total_document_bytes?: number
}

/** A slot a person is dropped into when the template is sent. */
export interface TemplateRole {
  id: string
  template_id: string
  name: string
  routing_order: number
  position: number
}

export interface TemplateField {
  id: string
  template_id: string
  role_id: string
  /** Which of the template's documents this box is on. */
  document_index: number
  kind: FieldKind
  label?: string
  options?: string[]
  page: number
  norm_x: number
  norm_y: number
  norm_w: number
  norm_h: number
  required: boolean
  /** Hide this box until another of the template's boxes, named by its id, has an answer. */
  show_when?: { field_id: string; op: string; value?: string }
}

/** One of a template's documents. */
export interface TemplateItem {
  id: string
  template_id: string
  position: number
  name: string
  document_key: string
  document_sha256: string
  document_bytes: number
  page_count: number
}

export interface TemplateDetail {
  template: Template
  items: TemplateItem[]
  roles: TemplateRole[]
  fields: TemplateField[]
  /**
   * Why this template can no longer be sent, when it cannot: GET
   * /api/templates/{id} sets it, and a send of it is refused with 400
   * template_needs_rebuilding and the same sentence. The server is the one
   * judge of a stored template, so the page shows `message` and holds Send
   * rather than running a check of its own.
   */
  needs_rebuilding?: TemplateProblem
}

/**
 * A stored template saved before a rule that now refuses it: a role with
 * nothing to sign, a box that waits on another role's box, or a box whose
 * "Show only when" leads back to itself, directly or through the role's
 * other boxes (review 6, carry-self-show-when). No route edits a template,
 * and `message` says how to make it again. The same object comes with a
 * Save as template refused 409 cannot_be_a_template. The page shows
 * `message` whatever the code; the codes are the store's
 * (internal/store/template.go problem), and a test holds the two lists equal.
 */
export interface TemplateProblem {
  code: 'role_without_signature' | 'rule_on_another_person' | 'rule_loops'
  role: string
  box?: string
  message: string
}

export interface CatalogSummary {
  slug: string
  industry: string
  name: string
  description: string
  sort_order: number
  page_count: number
  document_bytes: number
  role_count: number
  field_count: number
  legal_note: string
}

export interface CatalogField {
  kind: FieldKind
  label?: string
  options?: string[]
  page: number
  norm_x: number
  norm_y: number
  norm_w: number
  norm_h: number
  required: boolean
}

export interface CatalogRole {
  id: string
  name: string
  routing_order: number
  fields: CatalogField[]
}

export interface CatalogDetail {
  entry: {
    slug: string
    industry: string
    name: string
    description: string
    legal_note: string
    roles: CatalogRole[]
  }
  page_count: number
  document_bytes: number
  document_sha256: string
}

export interface RoleAssignment {
  role_id: string
  name: string
  email: string
  auth_requirements?: { methods: string[] }
}

/** ApiError carries the server's human-readable message and its machine code. */
export class ApiError extends Error {
  code: string
  status: number
  /**
   * What a refused one-time code leaves the page able to say: how many tries
   * remain before the code is finished, and, on a 428, what the sender asked
   * for. Both come off the body rather than being guessed from the status.
   */
  attemptsLeft?: number
  stepUp?: { method: 'email_otp'; hint?: string }
  /**
   * Which limit a plan_limit_exceeded is about (active_envelopes,
   * monthly_completions, trial_documents, retention, sender_seats). Part of
   * the API: a page branches on it, never on the sentence.
   */
  limit?: string
  /**
   * Every organization that stops an account deletion, on a 409
   * account_deletion_blocked, and where to write when a legal hold is what
   * stops it. Read off the body, so the page names each organization and its
   * reason rather than the one sentence the refusal opens with.
   */
  blockers?: AccountBlocker[]
  supportEmail?: string
  constructor(message: string, code: string, status: number) {
    super(message)
    this.code = code
    this.status = status
  }
}

/**
 * The web's sentence for a trial at its cap.
 *
 * A trial counts every document out for signature as though it were already
 * completed, so its cap is not a month's completions and does not lift on the
 * first of next month while what is out still counts. The server answers it
 * with its own limit, trial_documents, but in the web's voice it still says
 * the monthly sentence ("It resets at the start of next month"), which is
 * false during a trial. This is what is true, and it says nothing about
 * buying.
 */
export const TRIAL_AT_ITS_CAP =
  'This organization has as many documents out for signature or completed this month as its trial allows.'

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = `Request failed (${res.status})`
    let code = 'unknown'
    let attemptsLeft: number | undefined
    let stepUp: { method: 'email_otp'; hint?: string } | undefined
    let limit: string | undefined
    let blockers: AccountBlocker[] | undefined
    let supportEmail: string | undefined
    try {
      const body = await res.json()
      if (body?.error) message = body.error
      if (body?.code) code = body.code
      if (typeof body?.attempts_left === 'number') attemptsLeft = body.attempts_left
      if (body?.step_up?.method) stepUp = body.step_up
      if (typeof body?.limit === 'string') limit = body.limit
      if (Array.isArray(body?.organizations)) blockers = body.organizations
      if (typeof body?.support_email === 'string' && body.support_email) supportEmail = body.support_email
    } catch {
      // A non-JSON error body is not worth surfacing raw.
    }
    if (code === 'plan_limit_exceeded' && limit === 'trial_documents') message = TRIAL_AT_ITS_CAP
    const err = new ApiError(message, code, res.status)
    err.attemptsLeft = attemptsLeft
    err.stepUp = stepUp
    err.limit = limit
    err.blockers = blockers
    err.supportEmail = supportEmail
    throw err
  }
  return res.json() as Promise<T>
}

export async function uploadDocument(file: File): Promise<UploadResult> {
  const form = new FormData()
  form.append('file', file)
  return handle<UploadResult>(await fetch(apiUrl('/api/documents'), { ...creds, method: 'POST', body: form }))
}

/** A further PDF of an envelope or template: already uploaded, and named. */
export interface ExtraDocumentInput {
  name: string
  document_key: string
}

export async function createEnvelope(input: {
  organization_id: string
  title: string
  document_key: string
  extra_documents?: ExtraDocumentInput[]
  recipients: EnvelopeRecipientInput[]
  retention_days?: number
  routing_rules?: RoutingRuleInput[]
  /**
   * Ask docsdone to email each link itself rather than hand it back. It changes
   * what the record can claim: a link docsdone sent to an address is evidence
   * about that mailbox, and one handed to the sender is evidence about nothing
   * but the sender.
   */
  deliver_links?: boolean
  /** Absent means on: docsdone reminds the signers at 1, 3 and 7 days. */
  reminders_enabled?: boolean
}): Promise<CreateEnvelopeResult> {
  return handle<CreateEnvelopeResult>(
    await fetch(apiUrl('/api/envelopes'), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}

/**
 * One document somebody was sent, and what it wants from them.
 *
 * `waiting_on_me` is an obligation, not an unread marker: it is true when this
 * person still has to act AND nobody is ahead of them in the routing order.
 * `my_status` is their own part of it, which is not the envelope's status — an
 * envelope can be open while your half of it is finished.
 */
export interface InboxItem extends Envelope {
  role: string
  my_status: 'pending' | 'viewed' | 'signed' | 'declined'
  waiting_on_me: boolean
  /** The organization that sent it — the first thing a recipient wants. */
  sent_by_organization: string
}

export async function getInbox(): Promise<{ envelopes: InboxItem[]; waiting_on_you: number }> {
  return handle(await fetch(apiUrl('/api/inbox'), { ...creds, cache: 'no-store' }))
}

/**
 * Withdraw a document that is still out for signature.
 *
 * The reason is required and is written into the chain beside who withdrew it,
 * because "withdrawn" with no answer to "by whom, and why" is worth less than
 * nothing on a document somebody was asked to sign. Nothing is deleted: the
 * links stop working and the record gains one more entry.
 */
export async function voidEnvelope(id: string, reason: string): Promise<void> {
  await handle(
    await fetch(apiUrl(`/api/envelopes/${encodeURIComponent(id)}/void`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }),
  )
}

/**
 * Everything the send form needs to start again from a document already sent.
 *
 * There is no draft state — StatusDraft has been in the schema since the first
 * migration and nothing writes it — so a duplicate cannot be a half-made
 * envelope sitting somewhere. It is the send form, opened with the last one
 * filled in, and the sender presses send. That is also the safer shape: a
 * duplicate that sent itself would re-send to the same people.
 *
 * The PDF is fetched and re-uploaded rather than referenced. Blobs are
 * content-addressed, so the same bytes produce the same key and the second
 * upload stores nothing new; what it buys is the page geometry and the
 * supports_fields answer, which the envelope row does not carry.
 */
export async function envelopeToResend(id: string): Promise<{
  /** Every document it carried, in order. The first is the envelope's own. */
  documents: { name: string; upload: UploadResult; url: string }[]
  title: string
  /** Who it went to, in order, a signing group as one party (resendParties). */
  parties: ResendParty[]
  /** Each box's document, by position in `documents`. */
  fields: (Field & { document_index: number })[]
  /** The routing rules it was sent with, if any (resentRouting reads them). */
  routingRules?: RoutingRuleView[]
}> {
  const detail = await getEnvelope(id)
  const documents = await Promise.all(detail.documents.map(async (item) => {
    const res = await fetch(documentUrl(id, item.position), creds)
    if (!res.ok) throw new ApiError('Could not read the original document.', 'document_unreadable', res.status)
    const blob = await res.blob()
    const form = new FormData()
    form.append('file', new File([blob], `${item.name}.pdf`, { type: 'application/pdf' }))
    return {
      name: item.name,
      upload: await handle<UploadResult>(
        await fetch(apiUrl('/api/documents'), { ...creds, method: 'POST', body: form }),
      ),
      url: URL.createObjectURL(blob),
    }
  }))
  // A field written before an envelope could hold more than one document has
  // no item id, and belongs to the first.
  const at = new Map(detail.documents.map((d) => [d.id, d.position]))
  return {
    documents,
    title: detail.envelope.title,
    parties: resendParties(detail.participations),
    fields: detail.fields.map((f) => ({ ...f, document_index: at.get(f.envelope_item_id) ?? 0 })),
    routingRules: detail.routing?.rules,
  }
}

/** One party as an envelope was sent to them: a person, or a signing group with its members. */
export interface ResendParty {
  /** The person's name, or the group's label. */
  name: string
  /** The person's address; empty for a group, whose members each have one. */
  email: string
  role: RecipientRole
  routing_order: number
  members?: { name: string; email: string }[]
  /** Every participation the party was: one for a person, each member's for a group. */
  participation_ids: string[]
}

/**
 * The parties of a sent envelope, in their order, as the sending page sends
 * them.
 *
 * The server keeps a signing group as one participation per member, sharing
 * the group's id and label, with the group's boxes on its first member. Read
 * one participation at a time, Send again made each member a signer of their
 * own, the later ones with nothing to sign, which the page then refused to
 * send: the group could not be sent again as a group at all.
 */
export function resendParties(participations: Participation[]): ResendParty[] {
  const ordered = participations
    .filter((p) => p.role !== 'sender')
    .sort((a, b) => a.routing_order - b.routing_order || a.position - b.position)
  const parties: ResendParty[] = []
  const groups = new Map<string, ResendParty>()
  for (const p of ordered) {
    const member = { name: p.name, email: p.identifier }
    const group = p.signing_group_id ? groups.get(p.signing_group_id) : undefined
    if (group) {
      group.members?.push(member)
      group.participation_ids.push(p.id)
      continue
    }
    const party: ResendParty = p.signing_group_id
      ? {
          name: p.signing_group_label ?? '', email: '', role: p.role as RecipientRole,
          routing_order: p.routing_order, members: [member], participation_ids: [p.id],
        }
      : {
          name: p.name, email: p.identifier, role: p.role as RecipientRole,
          routing_order: p.routing_order, participation_ids: [p.id],
        }
    if (p.signing_group_id) groups.set(p.signing_group_id, party)
    parties.push(party)
  }
  return parties
}

/**
 * Whether an envelope went to everybody at once: two or more parties, all in
 * one turn. Send again starts its form that way, rather than one after
 * another, so the same people are asked in the same way again. The phone
 * app's Send again does the same.
 */
export function sentAtOnce(parties: Pick<ResendParty, 'routing_order'>[]): boolean {
  return parties.length >= 2 && parties.every((p) => p.routing_order === parties[0].routing_order)
}

export async function listEnvelopes(query?: string): Promise<{ envelopes: Envelope[] }> {
  const q = query?.trim() ? `?q=${encodeURIComponent(query.trim())}` : ''
  return handle(await fetch(apiUrl(`/api/envelopes${q}`), creds))
}

export async function getEnvelope(id: string): Promise<EnvelopeDetail> {
  return handle(await fetch(apiUrl(`/api/envelopes/${encodeURIComponent(id)}`), creds))
}

// ---------------------------------------------------------------- signing

/** Reads the invitation. Safe to call at any time: it records nothing. */
export async function getInvitation(token: string): Promise<Invitation> {
  return handle(await fetch(apiUrl(`/api/sign/${encodeURIComponent(token)}`), creds))
}

/** What the server says after it has put a one-time code in a mailbox. */
export interface Challenge {
  challenge_id: string
  expires_at: string
  /** The address with most of it removed, so the right mailbox can be opened. */
  sent_to_hint: string
}

/**
 * Asks docsdone to email a one-time code. The code is never in the answer:
 * that is the whole point of it, and the only way to hold one is to be able to
 * read the mailbox it went to.
 */
export async function requestChallenge(token: string): Promise<Challenge> {
  return handle<Challenge>(
    await fetch(apiUrl(`/api/sign/${encodeURIComponent(token)}/challenge`), { ...creds, method: 'POST' }),
  )
}

/**
 * Exchanges the invitation for a signing session. The server answers with the
 * session cookie, so nothing after this works until it has been called.
 *
 * When the sender asked for a one-time code, the challenge and the six digits
 * go with it; without them the server answers 428 and says so.
 */
export async function openSigningSession(
  token: string,
  step?: { challenge_id: string; code: string },
): Promise<SignSession> {
  return handle<SignSession>(
    await fetch(apiUrl(`/api/sign/${encodeURIComponent(token)}/session`), {
      ...creds,
      method: 'POST',
      ...(step ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(step) } : {}),
    }),
  )
}

/**
 * Records that the signer was shown the document. A POST because it is a
 * write: a GET that recorded a view would credit a mail scanner with reading.
 */
export async function recordViewed(
  token: string,
  /** Which document was on screen. The record names it, so it has to be right. */
  documentIndex?: number,
): Promise<{ viewed: boolean; completed?: boolean; certificate_ready?: boolean }> {
  return handle(await fetch(
    apiUrl(`/api/sign/${encodeURIComponent(token)}/view${at(documentIndex)}`),
    { ...creds, method: 'POST' },
  ))
}

export async function consent(token: string): Promise<void> {
  await handle(await fetch(apiUrl(`/api/sign/${encodeURIComponent(token)}/consent`), { ...creds, method: 'POST' }))
}

export interface SignResult {
  envelope_id: string
  status: string
  signed_sha256: string
  signed_bytes: number
  verification: VerificationReport
  retention_expires_at?: string
  /** False when the signature is recorded but other people still have to sign. */
  completed: boolean
  waiting_on?: string[]
  /** True once the document is finished and the certificate has been issued. */
  certificate_ready: boolean
}

export async function signEnvelope(
  token: string,
  signaturePng: string,
  typedName: string,
  /** Non-signature field values, keyed by field id. */
  fieldValues: Record<string, string> = {},
): Promise<SignResult> {
  return handle<SignResult>(
    await fetch(apiUrl(`/api/sign/${encodeURIComponent(token)}/sign`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        signature_png: signaturePng,
        typed_name: typedName,
        field_values: fieldValues,
      }),
    }),
  )
}

/**
 * Ends the signing session this browser holds on a link: the server marks it
 * ended and clears its cookie. The link itself keeps working until it
 * expires, and opening it again is a new session and a new act on the record.
 * Nothing called this, so a session lived until it lapsed, on whatever
 * computer it was opened.
 */
export async function endSigningSession(token: string): Promise<void> {
  await handle(
    await fetch(apiUrl(`/api/sign/${encodeURIComponent(token)}/session/end`), { ...creds, method: 'POST' }),
  )
}

export async function declineEnvelope(token: string, reason: string): Promise<void> {
  await handle(
    await fetch(apiUrl(`/api/sign/${encodeURIComponent(token)}/decline`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }),
  )
}

export async function verifyFile(file: File): Promise<VerificationReport> {
  const form = new FormData()
  form.append('file', file)
  return handle<VerificationReport>(await fetch(apiUrl('/api/verify'), { ...creds, method: 'POST', body: form }))
}

/**
 * Which of an envelope's documents a URL is for.
 *
 * Absent means the first, so every link written when an envelope held exactly
 * one document still asks for the one it meant.
 */
const at = (n?: number) => (n ? `?document=${n}` : '')

export const documentUrl = (id: string, n?: number) =>
  apiUrl(`/api/envelopes/${encodeURIComponent(id)}/document${at(n)}`)
export const certificateUrl = (id: string) => apiUrl(`/api/envelopes/${encodeURIComponent(id)}/certificate`)
export const signerCertificateUrl = (token: string) => apiUrl(`/api/sign/${encodeURIComponent(token)}/certificate`)
export const signedUrl = (id: string, n?: number) =>
  apiUrl(`/api/envelopes/${encodeURIComponent(id)}/signed${at(n)}`)
export const signerDocumentUrl = (token: string, n?: number) =>
  apiUrl(`/api/sign/${encodeURIComponent(token)}/document${at(n)}`)

// ---------------------------------------------------------------- session

/**
 * What the server knows about the caller. A session is a row in the database
 * behind an opaque cookie; the service key can also be exchanged for one
 * through the operator form. Google sign-in is offered once it is configured.
 */
export type Session =
  | {
      authenticated: true
      identity: {
        id: string
        /** The person behind the identity: what the member roster is keyed on. */
        party_id: string
        kind: 'person' | 'service'
        display_name: string
        email?: string
      }
      memberships: { organization_id: string; organization_name: string; role: 'admin' | 'member' }[]
      /** One entry per membership, in the same order. Admins receive the full payload. */
      billing?: OrgBillingSummary[]
      /**
       * How this session was signed in: email_otp for a code, federated_oidc
       * for Google or a company sign-in, service_credential for the operator
       * key. It was typed as the last two only, and a code sign-in answers
       * the first, so every reader was told a value could not occur that
       * most sessions carry.
       */
      auth_method: AuthMethod
      expires_at?: string
      /**
       * Where to send somebody who has just proved a second credential in a
       * browser already signed in as somebody else. The session described here
       * is the one they already had: the new one is held by the server, and
       * nothing is joined until they say so.
       */
      join_offered?: string
      /** Federated sign-in still needs an explicit terms acceptance once. */
      terms_required?: boolean
      terms_url?: string
      /**
       * Where a person can write for help docsdone's screens cannot give,
       * such as an account a legal hold keeps. Only when the server is
       * configured with one: an address nobody reads is worse than none.
       */
      support_email?: string
      /** No customer org yet — complete POST /api/signup/organization. */
      needs_signup?: boolean
    }
  | {
      authenticated: false
      sign_in: { google: boolean; email: boolean; enterprise: boolean; service: true }
      terms_url?: string
    }

/** The session as it is once the shell has an authenticated one to hand on. */
export type AuthenticatedSession = Extract<Session, { authenticated: true }>

/**
 * Ask for a sign-in code.
 *
 * This resolves the same way whatever the server did with the address. An
 * address that may sign in, one that may not, and one that has already asked
 * for its codes this hour all answer identically, on purpose — the form must
 * not become a way of asking docsdone who its customers are. So there is
 * nothing here to branch on, and the caller must not invent one.
 */
export async function startEmailSignIn(email: string): Promise<{ expires_in_seconds: number }> {
  return handle(
    await fetch(apiUrl('/api/auth/email/start'), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }),
  )
}

/** Exchange the six digits for a session. */
export async function verifyEmailSignIn(email: string, code: string, acceptTerms: boolean): Promise<Session> {
  return handle(
    await fetch(apiUrl('/api/auth/email/verify'), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, accept_terms: acceptTerms }),
    }),
  )
}

export async function acceptTerms(): Promise<void> {
  await handle(
    await fetch(apiUrl('/api/auth/terms/accept'), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accept_terms: true }),
    }),
  )
}

export async function getSession(): Promise<Session> {
  return handle(await fetch(apiUrl('/api/auth/session'), { ...creds, cache: 'no-store' }))
}

export interface Organization {
  id: string
  kind: string
  name: string
  plan: 'solo' | 'team' | 'business' | 'enterprise'
  role: 'admin' | 'member'
  created_at: string
}

export async function listOrganizations(): Promise<{ organizations: Organization[] }> {
  return handle(await fetch(apiUrl('/api/orgs'), creds))
}

/** One membership, as an administrator of the organization sees it. */
export interface OrgMember {
  party_id: string
  display_name: string
  kind: string
  role: 'admin' | 'member'
  created_at: string
}

/**
 * One person the organization's senders have written to, with every address it
 * holds for them. This is belief, not evidence: erasing a contact removes what
 * the organization thinks it knows and leaves what its signers did.
 */
export interface Contact {
  id: string
  display_name: string
  created_at: string
  identifiers: { kind: string; value: string; verified_at?: string; verified_by?: string }[]
}

/** What an erasure says it removed. The envelope ids are for reading, not for calling back with. */
export interface ErasureReport {
  envelopes: string[]
  contacts: number
  members: number
  /**
   * Stored files the erasure could not remove after its records had already
   * committed. Not a failure — every row is gone and the chain is true — but a
   * leak an operator is owed the size of.
   */
  documents_remaining?: number
}

/**
 * Opens a customer organization. Only an administrator of the platform
 * organization may; the creator becomes an admin of the new one, and the
 * optional address receives an admin invitation.
 */
export async function createOrganization(input: {
  name: string
  initial_admin_email?: string
}): Promise<{ organization: Organization; invite?: unknown }> {
  return handle(
    await fetch(apiUrl('/api/orgs'), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}

export async function listMembers(orgId: string): Promise<{ members: OrgMember[] }> {
  return handle(await fetch(apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/members`), creds))
}

export async function setMemberRole(
  orgId: string,
  partyId: string,
  role: 'admin' | 'member',
): Promise<void> {
  await handle(
    await fetch(
      apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(partyId)}/role`),
      {
        ...creds,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      },
    ),
  )
}

export async function removeMember(orgId: string, partyId: string): Promise<void> {
  await handle(
    await fetch(
      apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(partyId)}`),
      { ...creds, method: 'DELETE' },
    ),
  )
}

/** Ends every session a member of this organization holds. */
export async function revokeMemberSessions(orgId: string, partyId: string): Promise<void> {
  const org = encodeURIComponent(orgId)
  const party = encodeURIComponent(partyId)
  await handle(
    await fetch(apiUrl(`/api/orgs/${org}/members/${party}/sessions/revoke`), {
      ...creds,
      method: 'POST',
    }),
  )
}

/**
 * An invitation to join an organization. It admits exactly the address it
 * names: consent from Google alone admits nobody, so this row is the whole of
 * the allowlist.
 *
 * `accepted_at` and `revoked_at` are both nullable and both terminal — an
 * invitation is open only while neither is set and `expires_at` is in the
 * future. The listing returns all of them, because "who did we invite" is a
 * question about the past as much as the present.
 */
export interface Invite {
  id: string
  organization_id: string
  email: string
  role: 'admin' | 'member'
  created_at: string
  expires_at: string
  accepted_at?: string
  revoked_at?: string
}

export async function listInvites(orgId: string): Promise<{ invites: Invite[] }> {
  return handle(await fetch(apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/invites`), creds))
}

/**
 * Invites an address. The role cannot exceed the inviter's own: a member
 * cannot mint an admin, and the store refuses it rather than trusting this.
 */
export async function createInvite(
  orgId: string,
  input: { email: string; role: 'admin' | 'member' },
): Promise<{ invite: Invite }> {
  return handle(
    await fetch(apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/invites`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}

/** Withdraws an invitation. An invitation already accepted cannot be undone this way. */
export async function revokeInvite(orgId: string, inviteId: string): Promise<void> {
  const org = encodeURIComponent(orgId)
  const invite = encodeURIComponent(inviteId)
  await handle(await fetch(apiUrl(`/api/orgs/${org}/invites/${invite}`), { ...creds, method: 'DELETE' }))
}

export async function listContacts(orgId: string): Promise<{ contacts: Contact[] }> {
  return handle(await fetch(apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/contacts`), creds))
}

/** One envelope a recipient was asked to act on. */
export interface RecipientEnvelope {
  id: string
  title: string
  status: string
  role: string
  created_at: string
}

/** People asked to act on a document. The sender's own contact is not among them. */
export async function listRecipients(orgId: string): Promise<{ recipients: Contact[] }> {
  return handle(await fetch(apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/recipients`), creds))
}

/** One recipient and the envelopes they were asked to act on, newest first. */
export async function getRecipient(
  orgId: string,
  contactId: string,
): Promise<{ recipient: Contact; envelopes: RecipientEnvelope[] }> {
  return handle(await fetch(
    apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/recipients/${encodeURIComponent(contactId)}`),
    creds,
  ))
}

export async function eraseContact(orgId: string, contactId: string): Promise<void> {
  const org = encodeURIComponent(orgId)
  const contact = encodeURIComponent(contactId)
  await handle(
    await fetch(apiUrl(`/api/orgs/${org}/contacts/${contact}/erase`), {
      ...creds,
      method: 'POST',
    }),
  )
}

export type ConnectedChat = {
  id: string
  client_name: string
  /**
   * The host the chat's connection was sent to: who holds it, whatever name
   * the chat gave itself. Empty for a chat connected before it was recorded.
   */
  redirect_host?: string
  created_at: string
  last_used_at?: string
  expires_at: string
  /**
   * The person the chat acts as, by the name the members list shows, on the
   * organization's list only (internal/api/mcp_connect.go). Absent on the
   * person's own list, where every chat is their own, and when no name is
   * known.
   */
  connected_by?: string
}

/** Chats an administrator can disconnect. The token itself is never returned. */
export async function listConnectedChats(orgId: string): Promise<{ grants: ConnectedChat[] }> {
  return handle<{ grants: ConnectedChat[] }>(
    await fetch(apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/mcp/grants`), creds),
  )
}

export async function revokeConnectedChat(orgId: string, grantId: string): Promise<void> {
  const res = await fetch(
    apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/mcp/grants/${encodeURIComponent(grantId)}/revoke`),
    { ...creds, method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
  )
  if (res.status === 204) return
  await handle(res)
}

/** A chat the signed-in person connected, at the organization it acts in. */
export type MyConnectedChat = ConnectedChat & { organization_id?: string }

/**
 * The chats this person connected, in every organization, whatever their
 * role: a member who is not an administrator must be able to see and end a
 * chat that acts as them.
 */
export async function listMyConnectedChats(): Promise<{ grants: MyConnectedChat[] }> {
  return handle<{ grants: MyConnectedChat[] }>(
    await fetch(apiUrl('/api/mcp/grants'), { ...creds, cache: 'no-store' }),
  )
}

export async function disconnectMyChat(grantId: string): Promise<void> {
  const res = await fetch(
    apiUrl(`/api/mcp/grants/${encodeURIComponent(grantId)}/revoke`),
    { ...creds, method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
  )
  if (res.status === 204) return
  await handle(res)
}

/**
 * The person approves a chat. The server answers with the address to send
 * the browser to; the code in it is exchanged by the chat, not by this page.
 */
export async function connectChat(body: {
  redirect_uri: string
  state: string
  code_challenge: string
  code_challenge_method?: string
  /**
   * The link's client_id. The server names the grant from what the chat
   * registered under it; no name is sent, and the server refuses a body that
   * carries one (review-3-docs R1).
   */
  client_id: string
  /** The link's own response_type. Absent is read as code; anything else is refused. */
  response_type?: string
  organization_id: string
}): Promise<{ redirect_uri: string }> {
  return handle<{ redirect_uri: string }>(
    await fetch(apiUrl('/api/mcp/connect'), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  )
}

/**
 * Whether Allow would accept this chat's link, asked before anything is
 * pressed. A refusal is an answer, not an error: it comes back as
 * `accepted: false` with the code Allow would refuse with
 * (internal/api/mcp_connect.go handleMCPConnectCheck). Nothing is made. An
 * accepted link comes with the name the chat registered under its client_id,
 * which is the name Allow puts on the grant: "A chat" when the id names no
 * registration.
 */
export type ChatLinkCheck =
  | { accepted: true; client_name?: string }
  | { accepted: false; code: string; error?: string }

export async function checkChatLink(clientID: string, redirectURI: string, responseType: string): Promise<ChatLinkCheck> {
  const q = new URLSearchParams({ client_id: clientID, redirect_uri: redirectURI })
  if (responseType) q.set('response_type', responseType)
  return handle<ChatLinkCheck>(
    await fetch(apiUrl(`/api/mcp/connect/check?${q.toString()}`), { ...creds, cache: 'no-store' }),
  )
}

/**
 * Whether the organization's dispute lock is on, by the rule its erasure
 * refuses by. An administrator's question; anybody else is refused.
 */
export async function getErasureHolds(orgId: string): Promise<{ dispute_lock: boolean }> {
  return handle(await fetch(apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/erasure`), creds))
}

/**
 * Erases the organization: its contacts, templates, invitations and documents.
 * The chains stay, and so does everything they say about who signed what. An
 * Account dispute lock refuses the whole thing with 409 dispute_lock.
 */
export async function eraseOrganization(orgId: string): Promise<ErasureReport> {
  return handle<ErasureReport>(
    await fetch(apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/erase`), {
      ...creds,
      method: 'POST',
    }),
  )
}

/**
 * Where "Continue with Google" sends the browser. A top-level navigation, not
 * a fetch: the server answers with a redirect to Google and sets the flow
 * cookie on the way. returnTo is the app path to come back to.
 */
export const googleStartUrl = (returnTo: string) =>
  apiUrl('/api/auth/google/start?return_to=' + encodeURIComponent(returnTo))

/**
 * Where a company's staff sign in: their own identity provider, through the
 * broker. The address is in the URL because the server resolves the connection
 * from its domain and will not take one from the caller — a connection id in a
 * query would let anybody send somebody else's browser to a provider of their
 * choosing.
 */
export const enterpriseStartUrl = (email: string, returnTo: string) =>
  apiUrl(
    '/api/auth/enterprise/start?email=' +
      encodeURIComponent(email) +
      '&return_to=' +
      encodeURIComponent(returnTo),
  )

// ---------------------------------------------------------------- domains

/**
 * A domain a company says is theirs, and the proof.
 *
 * Claiming one says "everybody with an address here signs in through our
 * identity provider". Unproved, that is a claim over a namespace somebody else
 * owns, so nothing routes to it until the record is published.
 */
export interface OrgDomain {
  id: string
  domain: string
  verified: boolean
  /** What to publish, and what it must contain. Shown until it is proved. */
  record: string
  value: string
}

export const orgDomains = (orgId: string) =>
  get<{ domains: OrgDomain[] }>(`/api/orgs/${encodeURIComponent(orgId)}/domains`).then((r) => r.domains)

export const claimDomain = (orgId: string, domain: string) =>
  send<{ domain: OrgDomain }>(`/api/orgs/${encodeURIComponent(orgId)}/domains`, 'POST', { domain }).then(
    (r) => r.domain,
  )

export const verifyDomain = (orgId: string, domainId: string) =>
  send<{ domain: OrgDomain }>(
    `/api/orgs/${encodeURIComponent(orgId)}/domains/${encodeURIComponent(domainId)}/verify`,
    'POST',
  ).then((r) => r.domain)

export const removeDomain = (orgId: string, domainId: string) =>
  send<{ domains: OrgDomain[] }>(
    `/api/orgs/${encodeURIComponent(orgId)}/domains/${encodeURIComponent(domainId)}`,
    'DELETE',
  ).then((r) => r.domains)

// ---------------------------------------------------------------- sign-in settings

/**
 * One way into a company: what the administrator gives their identity
 * provider, and what it gave back.
 */
export interface SSOConnection {
  id: string
  display_name: string
  /** "pending" until the identity provider's own details have been supplied. */
  status: string
  /** The product, as the administrator named it, in the words a certificate uses. */
  provider: string
  protocol: 'saml' | 'oidc'
  /** What the administrator pastes INTO their identity provider. */
  sign_in_url: string
  identifier: string
  /** What it gave back; empty while pending. A client secret is never here: the
   * provider holds it, and a settings page that read one back would put it in a
   * browser, a log and a screenshot. */
  issuer: string
  sso_url: string
}

export interface SSOSettings {
  /** Whether this DEPLOYMENT can offer company sign-in at all. */
  configured: boolean
  /** Whether this ORGANIZATION has been set up. */
  enabled: boolean
  /** Whether anybody can actually sign in yet: set up, and with a finished connection. */
  ready: boolean
  /** False when the provider could not be reached just now. The settings still
   * render, so an outage somewhere else does not look like a dead end here. */
  reachable: boolean
  domains: string[]
  connections: SSOConnection[]
}

async function get<T>(path: string): Promise<T> {
  return handle<T>(await fetch(apiUrl(path), creds))
}

async function send<T>(path: string, method: string, body?: unknown): Promise<T> {
  return handle<T>(
    await fetch(apiUrl(path), {
      ...creds,
      method,
      ...(body === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    }),
  )
}

async function sso(orgId: string, method: string, path = '', body?: unknown): Promise<SSOSettings> {
  return handle<SSOSettings>(
    await fetch(apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/sso${path}`), {
      ...creds,
      method,
      ...(body === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    }),
  )
}

export const ssoSettings = (orgId: string) => sso(orgId, 'GET')

/** Turn it on, or change which domains it covers. */
export const enableSSO = (orgId: string, domains: string[]) => sso(orgId, 'POST', '', { domains })

export const disableSSO = (orgId: string) => sso(orgId, 'DELETE')

export const createSSOConnection = (
  orgId: string,
  body: { display_name: string; provider: string; protocol: 'saml' | 'oidc' },
) => sso(orgId, 'POST', '/connections', body)

/** Supply what the identity provider published. SAML wants the sign-on URL and
 * the certificate; OIDC wants the client credentials. */
export const completeSSOConnection = (
  orgId: string,
  connectionId: string,
  body: {
    issuer: string
    sso_url?: string
    certificate?: string
    client_id?: string
    client_secret?: string
  },
) => sso(orgId, 'PUT', `/connections/${encodeURIComponent(connectionId)}`, body)

/** The way out of a mistake: the wrong product picked, or a provider replaced. */
export const removeSSOConnection = (orgId: string, connectionId: string) =>
  sso(orgId, 'DELETE', `/connections/${encodeURIComponent(connectionId)}`)

/**
 * What /discover answers: whether this address's DOMAIN belongs to a company
 * that signs in with its own identity provider.
 *
 * One field, deliberately. The company's name and the product behind its
 * connection would both be useful on a page that discovered as somebody typed
 * and could render "Continue with Okta"; this one discovers on submit, so the
 * next thing that happens is the redirect and there is no moment to show them
 * in. An endpoint anybody can reach does not return what nothing reads.
 */
export type EnterpriseDestination = { enterprise: boolean }

/**
 * Ask where an address should sign in.
 *
 * It answers on the domain, never on the address, so it says nothing about who
 * has an account. Which companies are customers is not a fact worth
 * protecting; which addresses have accounts is.
 *
 * A failure is not an error to show anybody: it means the page falls back to a
 * code, which is a way in that works. Somebody whose company uses an identity
 * provider must not be stranded on a sign-in page because the broker is having
 * a bad minute.
 */
export async function discoverEnterprise(email: string): Promise<EnterpriseDestination> {
  try {
    const res = await fetch(apiUrl('/api/auth/enterprise/discover?email=' + encodeURIComponent(email)), creds)
    if (!res.ok) return { enterprise: false }
    return (await res.json()) as EnterpriseDestination
  } catch {
    return { enterprise: false }
  }
}

/** The operator form: the service key, exchanged for a session. */
export async function loginWithServiceKey(secret: string): Promise<Session> {
  return handle(
    await fetch(apiUrl('/api/auth/service'), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ secret }),
    }),
  )
}

export async function logout(): Promise<void> {
  await handle(await fetch(apiUrl('/api/auth/logout'), { ...creds, method: 'POST' }))
}

/**
 * Ends every session of the person, whichever way each was signed in (a code,
 * Google, or a company's sign-in joined to the same person), wherever it
 * lives: another browser's cookie or the app. This one included, so the
 * server clears the cookie in the same answer. It disconnects every chat the
 * person connected too. The server revokes by party (RevokeSessionsForParty,
 * B13), so the page needs no caveat beside the button.
 */
export async function signOutEverywhere(): Promise<void> {
  await handle(await fetch(apiUrl('/api/auth/sessions/revoke-all'), { ...creds, method: 'POST' }))
}

// ---------------------------------------------------------------- account

/** Why an organization stops an account deletion. */
export type AccountBlockReason = 'last_admin' | 'sole_member' | 'legal_hold'

/**
 * One organization on the deletion page.
 *
 * `blocks_deletion` is last_admin while other people belong and nobody else
 * administers it, and sole_member while the person is its only member. A
 * sole_member organization stops the deletion only until its name is typed to
 * go with the account, unless `legal_hold` is set: a held document cannot be
 * erased, so naming it changes nothing.
 */
export interface AccountOrganization {
  organization_id: string
  organization_name: string
  role: 'admin' | 'member'
  blocks_deletion?: 'last_admin' | 'sole_member'
  legal_hold?: boolean
  /** Set with legal_hold when the organization's dispute lock is what holds it. */
  dispute_lock?: boolean
}

/**
 * What the deletion page shows before the button: the address to type,
 * whether the sign-in is too old to delete with, and every organization the
 * person will leave.
 */
export interface AccountPreview {
  address: string
  signed_in_at: string
  fresh_sign_in_required: boolean
  organizations: AccountOrganization[]
}

/** One organization a refused deletion names, and why. */
export interface AccountBlocker {
  organization_id: string
  organization_name: string
  reason: AccountBlockReason
  /** Set on a legal_hold that is the organization's dispute lock rather than a held document. */
  dispute_lock?: boolean
}

/** An organization the person runs alone, named to be erased with the account. */
export interface OrganizationToErase {
  organization_id: string
  /** As the person typed it. The server compares it with the name, trimmed. */
  name: string
}

export async function getAccount(): Promise<AccountPreview> {
  return handle(await fetch(apiUrl('/api/auth/account'), { ...creds, cache: 'no-store' }))
}

/**
 * Deletes the person this browser is signed in as. The server resolves who
 * that is from the session itself and takes nothing else about it from here:
 * the address is a confirmation, not an instruction.
 */
export async function deleteAccount(confirm: string, eraseOrganizations: OrganizationToErase[]): Promise<void> {
  await handle(
    await fetch(apiUrl('/api/auth/account/delete'), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm, erase_organizations: eraseOrganizations }),
    }),
  )
}

/**
 * Two accounts that may be the same person.
 *
 * Somebody proves who they are more than one way over time — a code, a Google
 * account, their employer's identity provider once the company connects one —
 * and each proof is its own account. `offered` is true only when this browser
 * has just proved a second one while signed in as the first, and it goes back
 * to false the moment either side answers or the parked sign-in expires.
 */
export interface JoinOffer {
  offered: boolean
  /** The account they are signed in as, and stay signed in as either way. */
  signed_in_as: string
  /** The account they have just proved as well. */
  also_proved: string
}

export async function getJoinOffer(): Promise<JoinOffer> {
  return handle(await fetch(apiUrl('/api/auth/join'), { ...creds, cache: 'no-store' }))
}

/** Make them one person. Only the person holding both may ask. */
export async function joinAccounts(): Promise<void> {
  await handle(await fetch(apiUrl('/api/auth/join'), { ...creds, method: 'POST' }))
}

/** They are two different people, or the answer is not now. */
export async function discardJoinOffer(): Promise<void> {
  await handle(await fetch(apiUrl('/api/auth/join'), { ...creds, method: 'DELETE' }))
}

// ---------------------------------------------------------------- templates

export async function listTemplates(): Promise<{ templates: Template[] }> {
  return handle(await fetch(apiUrl('/api/templates'), creds))
}

export async function getTemplate(id: string): Promise<TemplateDetail> {
  return handle(await fetch(apiUrl(`/api/templates/${encodeURIComponent(id)}`), creds))
}

export async function createTemplate(input: {
  organization_id: string
  name: string
  document_key: string
  extra_documents?: ExtraDocumentInput[]
  retention_days?: number
  roles: { name: string; routing_order: number; fields: EnvelopeFieldInput[] }[]
}): Promise<TemplateDetail> {
  return handle<TemplateDetail>(
    await fetch(apiUrl('/api/templates'), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}

export async function deleteTemplate(id: string): Promise<void> {
  const res = await fetch(apiUrl(`/api/templates/${encodeURIComponent(id)}`), {
    ...creds,
    method: 'DELETE',
  })
  // 204 has no body, so it never reaches handle().
  if (!res.ok) await handle(res)
}

export async function sendTemplate(
  id: string,
  input: {
    title?: string
    assignments: RoleAssignment[]
    retention_days?: number
    deliver_links?: boolean
    reminders_enabled?: boolean
  },
): Promise<CreateEnvelopeResult> {
  return handle<CreateEnvelopeResult>(
    await fetch(apiUrl(`/api/templates/${encodeURIComponent(id)}/send`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}

export async function listCatalog(industry?: string): Promise<{
  entries: CatalogSummary[]
  industries: string[]
}> {
  const q = industry ? `?industry=${encodeURIComponent(industry)}` : ''
  return handle(await fetch(apiUrl(`/api/catalog${q}`), creds))
}

export async function getCatalogEntry(slug: string): Promise<CatalogDetail> {
  return handle(await fetch(apiUrl(`/api/catalog/${encodeURIComponent(slug)}`), creds))
}

export async function sendCatalogEntry(
  slug: string,
  input: {
    organization_id: string
    title?: string
    assignments: RoleAssignment[]
    retention_days?: number
    /** As on a template's send and a new envelope: docsdone emails each link. */
    deliver_links?: boolean
    /** Absent means on; only meaningful when docsdone emails the links. */
    reminders_enabled?: boolean
  },
): Promise<CreateEnvelopeResult> {
  return handle<CreateEnvelopeResult>(
    await fetch(apiUrl(`/api/catalog/${encodeURIComponent(slug)}/send`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}

export async function forkCatalogEntry(
  slug: string,
  input: { organization_id: string; name?: string },
): Promise<TemplateDetail> {
  return handle<TemplateDetail>(
    await fetch(apiUrl(`/api/catalog/${encodeURIComponent(slug)}/fork`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}

/**
 * What a reissue gives back: the new link, in the same shape a freshly created
 * recipient carries. signing_url is present only for a link handed back to the
 * sender; sent_to and delivered only for one docsdone emailed.
 */
export interface ReissueResult {
  participation_id: string
  link: { delivery: Delivery; expires_at: string; revoked: false }
  signing_url?: string
  sent_to?: string
  delivered?: boolean
}

/**
 * Withdraws a signer's link and issues another. The old link, the sessions it
 * opened and any one-time code still outstanding all stop working. The new one
 * travels the way the old one did unless `deliver: 'email'` says otherwise.
 */
export async function reissueLink(
  envelopeId: string,
  participationId: string,
  input: { deliver?: 'email' } = {},
): Promise<ReissueResult> {
  const envelope = encodeURIComponent(envelopeId)
  const participation = encodeURIComponent(participationId)
  return handle<ReissueResult>(
    await fetch(apiUrl(`/api/envelopes/${envelope}/participations/${participation}/reissue-link`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}

/**
 * What "Remind now" gives back: where the reminder went, whether the provider
 * took it, and the fresh link's expiry, which is the original's. The earlier
 * link keeps working; a reminder is a second message, never a replacement.
 */
export interface RemindResult {
  participation_id: string
  sent_to: string
  delivered: boolean
  link: { delivery: Delivery; expires_at: string; revoked: false }
}

/**
 * Sends one reminder because the sender asked. Refused, with a sentence, when
 * the person has already acted, it is not their turn, the link was handed on
 * rather than emailed, or they were reminded in the last twelve hours.
 */
export async function remindNow(envelopeId: string, participationId: string): Promise<RemindResult> {
  const envelope = encodeURIComponent(envelopeId)
  const participation = encodeURIComponent(participationId)
  return handle<RemindResult>(
    await fetch(apiUrl(`/api/envelopes/${envelope}/participations/${participation}/remind`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }),
  )
}

/** Turns the automatic reminders for one envelope on or off. */
export async function setReminders(
  envelopeId: string,
  enabled: boolean,
): Promise<{ reminders_enabled: boolean }> {
  return handle(
    await fetch(apiUrl(`/api/envelopes/${encodeURIComponent(envelopeId)}/reminders`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }),
  )
}

// ---------------------------------------------------------------- billing

export interface BillingLimits {
  max_sender_seats: number
  max_active_envelopes: number
  max_completions_per_month: number
  max_retention_days?: number
}

export interface BillingSubscription {
  id: string
  organization_id: string
  stripe_subscription_id: string
  stripe_price_id: string
  plan: string
  status: string
  trial_end?: string
  current_period_end?: string
  cancel_at_period_end: boolean
  canceled_at?: string
  created_at?: string
}

export interface BillingComposition {
  sender_seats: number
  completions_per_month: number
  active_envelopes: number
  retention_days: number
  collaboration: boolean
  legal_hold: boolean
}

export interface OrgBillingSummary {
  organization_id: string
  plan?: string
  write_allowed?: boolean
  /**
   * The longest an organization may keep a document, in days, told to every
   * member on the session whenever a plan limits it (absent when billing is
   * off, for the platform's own organization, and for an enterprise
   * customer). The gate refuses a named period above it, so the sending page
   * offers nothing longer.
   */
  max_retention_days?: number
  subscription?: BillingSubscription
  limits?: BillingLimits
  composition?: BillingComposition
  dispute_lock?: boolean
  preset_slug?: string
  growth_hints?: string[]
  active_envelopes?: number
  completions_this_month?: number
  stripe_customer_id?: string
  /**
   * Whether a plan card's annual price can be chosen. A custom build is
   * billed monthly, and checkout refuses an annual price for one with
   * annual_not_available (internal/api/billing_handlers.go). Absent from a
   * server older than the field, which is read as available.
   */
  annual_available?: boolean
}

/**
 * The feature flags a plan carries, named one by one.
 *
 * This was `Record<string, boolean | number>`, which accepts any key — so
 * `f.sms_included` typechecked, came back undefined forever, and rendered the
 * one tier that includes SMS as the only tier without it. Every name here is a
 * json tag in internal/billing/catalogue.go, and a Go test holds the two
 * lists to each other. A typo is now a compile error rather than a silently
 * absent feature on a pricing page.
 */
export interface CatalogueFeatures {
  conditional_routing: boolean
  signing_groups: boolean
  branding: boolean
  reminders: boolean
  legal_hold: boolean
  free_view_only_seats: boolean
  sms_add_on: boolean
  sms_included_per_month: number
}

export interface CataloguePlan {
  plan: string
  custom?: boolean
  limits?: BillingLimits & { retention_years?: number }
  features: CatalogueFeatures
  prices: {
    monthly_cents?: number
    annual_cents?: number
    monthly_price_id?: string
    annual_price_id?: string
  }
}

export async function getBillingPlans(): Promise<{
  plans: CataloguePlan[]
  trial_completions_per_month: number
  trial_days: number
}> {
  return handle(await fetch(apiUrl('/api/billing/plans'), creds))
}

export async function getOrgBilling(orgId: string): Promise<OrgBillingSummary> {
  return handle(await fetch(apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/billing`), creds))
}

export async function createSignupOrganization(input: {
  name: string
  composition?: BillingComposition
  utm_source?: string
}): Promise<{
  organization: { id: string; name: string }
  composition: BillingComposition
  monthly_cents: number
  billing_path: string
}> {
  return handle(
    await fetch(apiUrl('/api/signup/organization'), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}

export async function composeOrgBilling(
  orgId: string,
  input: { composition: BillingComposition; apply: boolean },
): Promise<{ composition: BillingComposition; monthly_cents: number; applied?: boolean }> {
  return handle(
    await fetch(apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/billing/compose`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ composition: input.composition, apply: input.apply }),
    }),
  )
}

export async function startBillingCheckout(
  orgId: string,
  input: { price_id: string; success_url: string; cancel_url: string },
): Promise<{ url: string }> {
  return handle(
    await fetch(apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/billing/checkout-session`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}

export async function openBillingPortal(orgId: string, returnUrl: string): Promise<{ url: string }> {
  return handle(
    await fetch(apiUrl(`/api/orgs/${encodeURIComponent(orgId)}/billing/portal-session`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ return_url: returnUrl }),
    }),
  )
}

/** Turns an envelope that already exists into a reusable template. */
export async function saveEnvelopeAsTemplate(
  envelopeId: string,
  input: { name?: string },
): Promise<TemplateDetail> {
  return handle<TemplateDetail>(
    await fetch(apiUrl(`/api/envelopes/${encodeURIComponent(envelopeId)}/save-as-template`), {
      ...creds,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}
